import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, ChevronLeft, ChevronRight, CalendarDays, Ban, List, BellRing, Truck, CalendarClock } from "lucide-react";
import { PageHeader, Button, Card, CardContent, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError, type Appointment } from "@/lib/api";
import { WeekView, DayView, MonthView } from "./scheduling/CalendarView";
import { AppointmentForm } from "./scheduling/AppointmentForm";
import { AppointmentDetails } from "./scheduling/AppointmentDetails";
import { BlockedTimes } from "./scheduling/BlockedTimes";
import { BusinessAvailability } from "./scheduling/BusinessAvailability";
import { ReminderSettings } from "./scheduling/ReminderSettings";
import { DispatchSettings } from "./scheduling/DispatchSettings";
import { statusMeta, fmtDateTime, startOfWeek, startOfDay, endOfDay, addDays, MONTHS } from "./scheduling/constants";

type View = "week" | "day" | "month" | "list";
const VIEWS: { key: View; label: string }[] = [
  { key: "week", label: "Week" }, { key: "day", label: "Day" }, { key: "month", label: "Month" }, { key: "list", label: "List" },
];

function rangeFor(view: View, anchor: Date): { start: Date; end: Date; title: string } {
  if (view === "day") return { start: startOfDay(anchor), end: endOfDay(anchor), title: anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) };
  if (view === "month") {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return { start: gridStart, end: endOfDay(addDays(gridStart, 41)), title: `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}` };
  }
  // week + list (list shows the current week's upcoming)
  const s = startOfWeek(anchor);
  const e = endOfDay(addDays(s, 6));
  return { start: s, end: e, title: `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${addDays(s, 6).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` };
}

function ListView({ appts, onOpen }: { appts: Appointment[]; onOpen: (id: string) => void }) {
  const sorted = [...appts].sort((a, b) => a.start_at.localeCompare(b.start_at));
  if (sorted.length === 0) return <EmptyState icon={CalendarDays} title="No appointments" description="Nothing scheduled in this range." />;
  return (
    <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="list-view">
      {sorted.map((a) => {
        const m = statusMeta(a.status);
        return (
          <button key={a.id} onClick={() => onOpen(a.id)} data-testid={`list-row-${a.id}`}
            className={`flex w-full items-center justify-between border-l-4 ${m.bar} px-5 py-3.5 text-left hover:bg-slate-50`}>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900">{a.contact_name ?? "—"}</p>
              <p className="truncate text-xs text-slate-500">{a.service_name ?? a.requested_service ?? "Appointment"} · {fmtDateTime(a.start_at)}</p>
            </div>
            <StatusBadge tone={m.tone}>{m.label}</StatusBadge>
          </button>
        );
      })}
    </CardContent></Card>
  );
}

export default function Scheduling() {
  const [view, setView] = useState<View>("week");
  const [anchor, setAnchor] = useState(new Date());
  const [appts, setAppts] = useState<Appointment[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formDate, setFormDate] = useState<Date | null>(null);
  const [showBlocked, setShowBlocked] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [showDispatch, setShowDispatch] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  const { start, end, title } = useMemo(() => rangeFor(view, anchor), [view, anchor]);

  const load = useCallback(() => {
    setAppts(null);
    api.schedListAppointments({ start: start.toISOString(), end: end.toISOString() })
      .then(setAppts).catch((e) => toast.error(formatApiError(e)));
  }, [start, end]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const step = (dir: number) => {
    if (view === "day") setAnchor((a) => addDays(a, dir));
    else if (view === "month") setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + dir, 1));
    else setAnchor((a) => addDays(a, dir * 7));
  };
  const openNew = (d?: Date | null) => { setFormDate(d ?? anchor); setShowForm(true); };

  return (
    <div data-testid="page-scheduling" className="space-y-6">
      <PageHeader title="Scheduling" description="Book, reschedule and manage appointments — conflicts are prevented server-side." testid="scheduling-header"
        action={
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0">
            <Button variant="outline" onClick={() => setShowAvailability(true)} data-testid="availability-btn" className="shrink-0"><CalendarClock className="h-4 w-4" /> Availability</Button>
            <Button variant="outline" onClick={() => setShowDispatch(true)} data-testid="dispatch-settings-btn" className="shrink-0"><Truck className="h-4 w-4" /> Dispatch</Button>
            <Button variant="outline" onClick={() => setShowReminders(true)} data-testid="reminder-settings-btn" className="shrink-0"><BellRing className="h-4 w-4" /> Reminders</Button>
            <Button variant="outline" onClick={() => setShowBlocked(true)} data-testid="blocked-times-btn" className="shrink-0"><Ban className="h-4 w-4" /> Blocked times</Button>
            <Button onClick={() => openNew(null)} data-testid="new-appointment-btn" className="shrink-0"><Plus className="h-4 w-4" /> New appointment</Button>
          </div>
        } />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => step(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="sm" onClick={() => step(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <span className="ml-2 text-sm font-semibold text-slate-800" data-testid="cal-range-title">{title}</span>
        </div>
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-0.5" data-testid="view-switcher">
          {VIEWS.map((v) => (
            <button key={v.key} onClick={() => setView(v.key)} data-testid={`view-${v.key}`}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === v.key ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-800"}`}>
              {v.key === "list" ? <List className="h-3.5 w-3.5" /> : <CalendarDays className="h-3.5 w-3.5" />} {v.label}
            </button>
          ))}
        </div>
      </div>

      {appts === null ? <LoadingBlock rows={5} /> : (
        <>
          {view === "week" && <WeekView anchor={anchor} appts={appts} onOpen={setOpenId} onAddAt={openNew} />}
          {view === "day" && <DayView anchor={anchor} appts={appts} onOpen={setOpenId} onAddAt={openNew} />}
          {view === "month" && <MonthView anchor={anchor} appts={appts} onOpen={setOpenId} onDayClick={(d) => { setAnchor(d); setView("day"); }} />}
          {view === "list" && <ListView appts={appts} onOpen={setOpenId} />}
        </>
      )}

      {showForm && <AppointmentForm initialDate={formDate} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); reload(); }} />}
      {openId && <AppointmentDetails apptId={openId} onClose={() => setOpenId(null)} onChanged={reload} />}
      {showBlocked && <BlockedTimes onClose={() => { setShowBlocked(false); reload(); }} />}
      {showAvailability && <BusinessAvailability onClose={() => setShowAvailability(false)} />}
      {showReminders && <ReminderSettings onClose={() => setShowReminders(false)} />}
      {showDispatch && <DispatchSettings onClose={() => setShowDispatch(false)} />}
    </div>
  );
}
